import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del, tokenStorage } from '@/services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PolicyStatus = 'draft' | 'active' | 'archived';
export type PolicyCategory =
  | 'conduct'
  | 'leave'
  | 'benefits'
  | 'compensation'
  | 'safety'
  | 'technology'
  | 'compliance'
  | 'other';
export type PolicyLocation = 'global' | 'india_uh' | 'india_ec' | 'uae' | 'us' | 'uk';

export interface Policy {
  id: string;
  code: string;
  title: string;
  description?: string;
  category: PolicyCategory;
  status: PolicyStatus;
  version: string;
  effective_date: string;
  expiry_date?: string;
  owner_name?: string;
  country_codes?: string[];
  /** Backend column is `requires_acknowledgement`. Older code referenced
   *  this as `acknowledgement_required` — kept as an alias so consumers
   *  reading either name keep working. The list endpoint serializes
   *  `requires_acknowledgement`; map below normalises it. */
  requires_acknowledgement: boolean;
  acknowledgement_required: boolean;
  acknowledgement_deadline_days?: number;
  total_assigned: number;
  total_acknowledged: number;
  total_pending: number;
  total_overdue: number;
  acknowledgement_rate: number;
  content_url?: string;
  content?: string;
  document_url?: string;
  location: PolicyLocation;
  created_at: string;
  updated_at: string;
}

export interface PolicyCreatePayload {
  title: string;
  policy_code: string;
  version: string;
  description: string;
  category: PolicyCategory;
  location: PolicyLocation;
  effective_date: string;
  review_date?: string;
  owner_name: string;
  owner_email: string;
  document_url?: string;
  content?: string;
  content_summary?: string;
  requires_acknowledgement: boolean;
  acknowledgement_deadline_days: number;
  auto_assign_rules: Record<string, unknown>;
}

export interface PolicyListResponse {
  items: Policy[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  // aliases kept for backward compat
  per_page?: number;
  total_pages?: number;
}

export interface PolicyFilters {
  search?: string;
  category?: PolicyCategory | '';
  status?: PolicyStatus | '';
  country_code?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface PolicyAcknowledgement {
  id: string;
  policy_id: string;
  policy_title: string;
  policy_code: string;
  policy_version: string;
  employee_id: string;
  employee_name?: string;
  status: 'pending' | 'acknowledged' | 'overdue';
  assigned_at: string;
  deadline_at?: string;
  acknowledged_at?: string;
  ip_address?: string;
  reminder_count: number;
}

export interface AcknowledgePayload {
  policy_id: string;
}

export type PolicyUpdatePayload = Partial<PolicyCreatePayload>;

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const policyKeys = {
  all: ['policies'] as const,
  lists: () => [...policyKeys.all, 'list'] as const,
  list: (filters: PolicyFilters) => [...policyKeys.lists(), filters] as const,
  details: () => [...policyKeys.all, 'detail'] as const,
  detail: (id: string) => [...policyKeys.details(), id] as const,
  acknowledgements: (id: string) => [...policyKeys.detail(id), 'acknowledgements'] as const,
  myPolicies: () => [...policyKeys.all, 'my'] as const,
  distribution: (id: string) => [...policyKeys.detail(id), 'distribution'] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function usePolicies(filters: PolicyFilters = {}) {
  return useQuery({
    queryKey: policyKeys.list(filters),
    queryFn: async () => {
      const resp = await get<PolicyListResponse>('/policies', {
        params: {
          page: filters.page,
          page_size: filters.per_page,
          search: filters.search || undefined,
          category: filters.category || undefined,
          status: filters.status || undefined,
        },
      });
      // Backend serializes the column as `requires_acknowledgement`.
      // Mirror it onto the legacy `acknowledgement_required` field that
      // older UI code reads, so the HR list shows the correct label.
      return {
        ...resp,
        items: resp.items.map((p) => ({
          ...p,
          acknowledgement_required:
            (p as Policy & { requires_acknowledgement?: boolean }).requires_acknowledgement
              ?? p.acknowledgement_required
              ?? false,
        })),
      } as PolicyListResponse;
    },
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export function usePolicy(id: string) {
  return useQuery({
    queryKey: policyKeys.detail(id),
    queryFn: () => get<Policy & { policy_code?: string; content?: string; location?: string }>(`/policies/${id}`),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function usePolicyAcknowledgements(policyId: string) {
  return useQuery({
    queryKey: policyKeys.acknowledgements(policyId),
    queryFn: () => get<PolicyAcknowledgement[]>(`/policies/${policyId}/acknowledgements`),
    enabled: !!policyId,
  });
}

export interface MyPoliciesResponse {
  employee_id: string;
  items: PolicyAcknowledgement[];
  total_pending: number;
  total_overdue: number;
}

export function useMyPolicies() {
  return useQuery({
    queryKey: policyKeys.myPolicies(),
    queryFn: () => get<MyPoliciesResponse>('/policies/my'),
    staleTime: 30_000,
  });
}

export function useEnsureAcknowledgement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policy_id: string) =>
      post<PolicyAcknowledgement | null>(`/policies/${policy_id}/ensure-acknowledgement`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: policyKeys.myPolicies() });
    },
  });
}

export function useAcknowledgePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ policy_id }: AcknowledgePayload) =>
      post<PolicyAcknowledgement>(`/policies/${policy_id}/acknowledge`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: policyKeys.myPolicies() });
      void qc.invalidateQueries({ queryKey: policyKeys.lists() });
    },
  });
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PolicyCreatePayload) =>
      post<Policy>('/policies', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: policyKeys.lists() });
    },
  });
}

export function useUpdatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PolicyUpdatePayload }) =>
      patch<Policy>(`/policies/${id}`, payload),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: policyKeys.lists() });
      void qc.invalidateQueries({ queryKey: policyKeys.detail(id) });
    },
  });
}

export function usePublishPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Policy>(`/policies/${id}/publish`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: policyKeys.lists() });
      void qc.invalidateQueries({ queryKey: policyKeys.detail(id) });
    },
  });
}

export function useRetirePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Policy>(`/policies/${id}/retire`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: policyKeys.lists() });
      void qc.invalidateQueries({ queryKey: policyKeys.detail(id) });
    },
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<void>(`/policies/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: policyKeys.lists() });
    },
  });
}

export function useUploadPolicyDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      const token = tokenStorage.getAccess();
      const response = await fetch(`/api/v1/policies/${id}/upload-document`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({})) as { detail?: string };
        throw new Error(err.detail ?? 'Upload failed');
      }
      return response.json() as Promise<Policy>;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: policyKeys.lists() });
      void qc.invalidateQueries({ queryKey: policyKeys.detail(id) });
    },
  });
}

